import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "../Button";
import classNames from "classnames";
import SimpleBar from "simplebar-react";
import { Link } from "react-router-dom";
import routes from "@const/routes";
import { useTranslation } from "react-i18next";

import Modal from "../Modal";
import Avatar from "../Avatar";
import CreatorsBadges from "../CreatorsBadges";
import VerifiedUserIcon from "../VerifiedUserIcon";
import { useOffsetInfiniteScroll } from "@hooks/useOffsetInfiniteScroll";
import {
  getLikesList,
  getLikesListPortion,
  likesListReset,
} from "@redux/likesList/actions";
import { getPostDetails } from "@redux/postDetails/actions";
import { followersListLimit } from "@const/limits";
import { followCreator, unfollowCreator } from "@redux/creatorProfile/actions";

import styles from "./index.module.scss";

const FollowButtons = ({ user, buttonClassName, isFollowUpdating }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("buttons");

  const handleFollowClick = ({ isFollowed, id, username }) => {
    if (isFollowed) {
      dispatch(
        unfollowCreator({
          id,
          username,
        }),
      );
    } else {
      dispatch(
        followCreator({
          id,
          username,
          shouldFetchProfile: true,
          type: "data",
        }),
      );
    }
  };
  return (
    <Button
      color={user?.isFollowed ? "secondary" : "primary"}
      size="sm"
      disabled={isFollowUpdating}
      onClick={() => handleFollowClick(user)}
      className={classNames(
        "h-fit-content ms-2 ms-xl-auto my-xl-1 d-flex justify-content-center align-self-center w-100",
        styles.button,
        buttonClassName,
      )}
    >
      <span
        className={classNames(
          "text-body-2 text-medium",
          user?.isFollowed ? "text-white" : "text-black",
        )}
      >
        {t(user?.isFollowed ? "UNFOLLOW" : "FOLLOW")}
      </span>
    </Button>
  );
};

const Users = ({ data, isFollowUpdating, isMyPost, onClose, myId }) => {
  return data?.map(
    ({
      id,
      isSupporter,
      isMostProfitableSupporter,
      isPromoter,
      isMostProfitablePromoter,
      isDeleted,
      user,
    }) => (
      <div
        key={id}
        className={classNames(
          "text-decoration-none d-flex px-xl-2 py-1 pe-xl-4",
          styles.likingUser,
        )}
      >
        <div className={classNames(styles.me12, "me-xl-3")}>
          <Link
            onClick={onClose}
            to={`${routes.CREATOR_PROFILE()}${user?.username}`}
            className={classNames(
              (user?.isDeleted || isDeleted) && styles.disableLink,
            )}
          >
            <Avatar
              avatar={user?.avatar}
              className={styles.avatar}
              isDeleted={user?.isDeleted || isDeleted}
            />
          </Link>
        </div>

        <div
          className={classNames(
            styles.me12,
            "me-xl-3 flex-grow-1 flex-xl-grow-0",
          )}
        >
          <div
            className={classNames(
              "text-body-2 text-semibold text-primary d-flex align-items-center text-truncate",
              styles.username,
            )}
          >
            <Link
              onClick={onClose}
              to={`${routes.CREATOR_PROFILE()}${user?.username}`}
              className={classNames(
                styles.usernameWithChip,
                "text-truncate text-primary text-decoration-none",
                (user?.isDeleted || isDeleted || !isMyPost) &&
                  styles.disableLink,
              )}
            >
              {user?.username}
            </Link>

            <VerifiedUserIcon
              isVisible={user?.isIdentityVerified}
              className="me-2"
            />

            {(!isDeleted || !user?.isDeleted) && (
              <CreatorsBadges
                isSupporter={isSupporter || user?.isSupporter}
                isTopSupporter={
                  isMostProfitableSupporter || user?.isMostProfitableSupporter
                }
                isPromoter={isPromoter || user?.isPromoter}
                isTopPromoter={
                  isMostProfitablePromoter || user?.isMostProfitablePromoter
                }
              />
            )}
          </div>

          <Link
            onClick={onClose}
            to={`${routes.CREATOR_PROFILE()}${user?.username}`}
            className={classNames(
              "text-break text-body-2 text-decoration-none text-medium text-secondary text-truncate",
              styles.name,
            )}
          >
            {user?.name}
          </Link>
        </div>
        <FollowButtons
          user={{
            isFollowed: user?.isFollowed,
            username: user?.username,
            id,
            uniqueKey: user?.uniqueKey,
          }}
          buttonClassName={classNames(
            (id === myId || isDeleted || user?.isDeleted) &&
              "invisible d-xl-none",
          )}
          isFollowUpdating={isFollowUpdating}
        />
      </div>
    ),
  );
};

const LikesListModal = ({ id, uniqueKey, isOpen, onClose, isMyPost }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("modals");

  const { data, isLoading, nextOffset, totalCount, isFollowUpdating } =
    useSelector(({ likesListReducer }) => likesListReducer);

  const contentStyle = useMemo(
    () => (data.length < 7 ? styles.small : styles.big),
    [data],
  );

  const { id: myId } = useSelector(({ profileReducer }) => profileReducer);

  const { trigger } = useOffsetInfiniteScroll({
    isLoading,
    totalCount,
    limit: followersListLimit,
    offset: nextOffset,
    callback: (params) => dispatch(getLikesListPortion({ id, ...params })),
  });

  useEffect(() => {
    if (isOpen) {
      dispatch(getLikesList({ id, limit: followersListLimit, offset: 0 }));
    } else {
      dispatch(likesListReset());
    }
  }, [isOpen, dispatch, id]);

  useEffect(() => {
    return () => {
      dispatch(likesListReset());
      if (!isOpen) return;
      if (uniqueKey) dispatch(getPostDetails({ uniqueKey }));
    };
  }, []);

  return (
    <Modal
      showCancel
      onClose={onClose}
      title={t("LIKES")}
      isOpen={isOpen}
      submitDisabled={true}
      showConfirm={false}
      footerClassName="d-none"
      cancelClassName="btn-resetStyle pe-none text-body-2 text-medium"
      closeClassName={classNames("end-0", styles.close)}
      bodyClassName={classNames("px-0 ps-xl-3 pe-xl-1 pb-4")}
    >
      <SimpleBar className={classNames(styles.scroll, contentStyle)}>
        <Users
          data={data}
          onClose={onClose}
          isFollowUpdating={isFollowUpdating}
          isMyPost={isMyPost}
          myId={myId}
        />
        {!isLoading && trigger}
      </SimpleBar>
    </Modal>
  );
};

export default LikesListModal;
